import { merge } from "lodash";
import { EditorState, Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

import { decodeGlobalId } from "~/lib/relayUtil";
import WebsocketClient, { GroupEmitter } from "~/lib/websocket";

import { Extension } from "../extension";
import { getBlockBoundaryState } from "./blockBoundary";

export interface BlockExternalAttributes {
	linked: boolean;
}

type websocketBlockExternalAttributeMessage =
	| {
			type: "updateAttribute";
			updates: { [key: string]: Partial<BlockExternalAttributes> };
	  }
	| {
			type: "_other";
	  };

type AttributesMap = { [key: string]: BlockExternalAttributes | undefined };

class ExternalAttribute extends Extension {
	socket: GroupEmitter | null = null;
	static pluginKey = new PluginKey("externalAttribute");
	get name() {
		return "externalAttribute";
	}
	init() {
		const [, pk] = decodeGlobalId(this.options.documentId);
		this.socket = WebsocketClient.getInstance().getOrJoinGroup(`prose_data.${pk}`, {
			sessionId: this.options.sessionId,
			publicLinkId: this.options.publicLinkId,
			inviteId: this.options.inviteId,
		});
		// this reuses the same group as prose collabbecause there's no need to check_access twice.
		// TODO: make sure it really does not check_access twice.
		this.socket.on<websocketBlockExternalAttributeMessage>("message", this.onSocketMessage);
	}
	get plugins() {
		return [
			new Plugin<AttributesMap>({
				key: ExternalAttribute.pluginKey,
				props: {
					decorations: state => {
						const decos: Decoration[] = [];
						const attributes = ExternalAttribute.getAllAttributes(state);
						const blockBoundaryState = getBlockBoundaryState(state);
						if (!blockBoundaryState) return null;
						Object.entries(attributes || {}).forEach(([key, value]) => {
							if (key in blockBoundaryState.blockBoundaries) {
								const [start, end] = blockBoundaryState.blockBoundaries[key];
								decos.push(Decoration.node(start, end, {}, value));
							}
						});
						return DecorationSet.create(state.doc, decos);
					},
				},
				state: {
					init: (_config, state) => {
						const prevState = ExternalAttribute.pluginKey.getState(state);
						if (prevState) {
							return prevState;
						}
						return {};
					},
					apply: (tr, value, _oldState, _newState) => {
						const updates = tr.getMeta(ExternalAttribute.pluginKey);
						if (updates) {
							return merge({}, value, updates);
						}
						return value;
					},
				},
			}),
		];
	}
	onSocketMessage = (msg: websocketBlockExternalAttributeMessage) => {
		const view = this.editorView;
		if (!view) return;
		if (msg.type === "updateAttribute") {
			view.dispatch(view.state.tr.setMeta(ExternalAttribute.pluginKey, msg.updates));
		}
	};
	destroy() {
		this.socket?.off("message", this.onSocketMessage);
		this.socket?.close();
		this.socket = null;
	}
	static getAllAttributes(state: EditorState) {
		return ExternalAttribute.pluginKey.getState(state) as AttributesMap | undefined;
	}
	static getBlockAttributes(state: EditorState, blockKey: string) {
		const attrs = ExternalAttribute.getAllAttributes(state);
		return attrs ? attrs[blockKey] : undefined;
	}
}

export default ExternalAttribute;
